import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { format, subDays, differenceInDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface StudyMetric {
  studyId: string;
  studyCode: string;
  timescaleStudyId: string;
  avgAdherence: number;
  totalParticipants: number;
  totalAlerts: number;
  avgAlertsPerDay: number;
  participantsWithAlerts: string[];
  numParticipantsWithAlerts: number;
}

const StudiesMetricsTable: React.FC = () => {
  const [data, setData] = useState<StudyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(() =>
    format(subDays(new Date(), 15), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sortBy, setSortBy] = useState<string>("default");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (!isInitialized) {
      fetchStudiesMetrics(startDate, endDate);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchStudiesMetrics = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.get(
        `${API_ENDPOINTS.ADHERENCE_MONITOR.STUDIES_METRICS}?startDate=${start}&endDate=${end}`
      );

      if (response.success) {
        setData(response.data || []);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching studies metrics:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchStudiesMetrics(startDate, endDate);
  };

  const handleResetDateRange = (days: number) => {
    const newStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const newEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchStudiesMetrics(newStart, newEnd);
  };

  const handleShowParticipants = (participants: string[]) => {
    setSelectedParticipants(participants);
    setParticipantsDialogOpen(true);
  };

  const handleCloseParticipantsDialog = () => {
    setParticipantsDialogOpen(false);
    setSelectedParticipants([]);
  };

  const getSortedData = useMemo(() => {
    if (sortBy === "default") {
      return [...data];
    }

    const sorted = [...data].sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (sortBy) {
        case "studyCode":
          aValue = a.studyCode;
          bValue = b.studyCode;
          break;
        case "timescaleDbId":
          aValue = a.timescaleStudyId;
          bValue = b.timescaleStudyId;
          break;
        case "avgAdherence":
          aValue = a.avgAdherence;
          bValue = b.avgAdherence;
          break;
        case "totalAlerts":
          aValue = a.totalAlerts;
          bValue = b.totalAlerts;
          break;
        case "avgAlerts":
          aValue = a.avgAlertsPerDay;
          bValue = b.avgAlertsPerDay;
          break;
        case "numParticipants":
          aValue = a.numParticipantsWithAlerts;
          bValue = b.numParticipantsWithAlerts;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [data, sortBy, sortOrder]);

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{ fontWeight: 600, color: "#8C1515" }}
          >
            Studies Metrics
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Complete metrics for all studies including adherence, alerts, and
            participants
          </Typography>
        </Box>

        {/* Date Range Controls - Row 1 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          {/* Left side: Date controls */}
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              onClick={handleApplyDateRange}
              sx={{
                mt: 1,
                backgroundColor: "#8C1515",
                "&:hover": { backgroundColor: "#6B0E0E" },
              }}
            >
              Apply
            </Button>
          </Stack>

          {/* Right side: Sort dropdown */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
              size="small"
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="studyCode">Study Code</MenuItem>
              <MenuItem value="timescaleDbId">TimescaleDB ID</MenuItem>
              <MenuItem value="avgAdherence">Avg Adherence</MenuItem>
              <MenuItem value="totalAlerts">Total Alerts</MenuItem>
              <MenuItem value="avgAlerts">Avg Alerts/Day</MenuItem>
              <MenuItem value="numParticipants">
                Participants with Alerts
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Date range error message */}
        {dateRangeError && (
          <Typography sx={{ color: "#F44336", fontSize: 12, mb: 1 }}>
            {dateRangeError}
          </Typography>
        )}

        {/* Preset Buttons and Order Selector - Row 2 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 3,
          }}
        >
          {/* Preset buttons on the left */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(7)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 7 Days
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(15)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 15 Days
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(30)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 30 Days
            </Button>
          </Stack>

          {/* Order selector on the right (invisible when default sort) */}
          <FormControl
            sx={{
              minWidth: 120,
              visibility: sortBy === "default" ? "hidden" : "visible",
            }}
          >
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              label="Order"
              size="small"
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Note */}
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 2 }}
        >
          * Click on the number in "Participants with Alerts" column to view the
          list of participants responsible for the alerts.
        </Typography>

        {/* Table */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data.length > 0 ? (
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#FEF7F7" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Study Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>TimescaleDB ID</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Avg Adherence %
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total Alerts
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Avg Alerts/Day
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Participants with Alerts*
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedData.map((study, index) => (
                  <TableRow
                    key={study.studyId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
                      "&:hover": { backgroundColor: "#FEF7F7" },
                    }}
                  >
                    <TableCell>{study.studyCode}</TableCell>
                    <TableCell>{study.timescaleStudyId}</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          backgroundColor:
                            study.avgAdherence > 90
                              ? "#E8F5E9"
                              : study.avgAdherence >= 80
                              ? "#E0F2F1"
                              : study.avgAdherence >= 70
                              ? "#F3E5F5"
                              : "#FFF3E0",
                          borderRadius: 1,
                          display: "inline-block",
                          fontWeight: 600,
                        }}
                      >
                        {study.avgAdherence}%
                      </Box>
                    </TableCell>
                    <TableCell align="right">{study.totalAlerts}</TableCell>
                    <TableCell align="right">{study.avgAlertsPerDay}</TableCell>
                    <TableCell align="center">
                      {study.numParticipantsWithAlerts > 0 ? (
                        <Button
                          size="small"
                          onClick={() =>
                            handleShowParticipants(study.participantsWithAlerts)
                          }
                          sx={{
                            color: "#8C1515",
                            textDecoration: "underline",
                            "&:hover": { backgroundColor: "#FEF7F7" },
                          }}
                        >
                          {study.numParticipantsWithAlerts}
                        </Button>
                      ) : (
                        <Typography variant="body2">0</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: "#8C1515",
                          color: "#8C1515",
                          "&:hover": {
                            backgroundColor: "#FEF7F7",
                            borderColor: "#6B0E0E",
                          },
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          !loading && (
            <Alert severity="info">
              No studies found for the selected date range.
            </Alert>
          )
        )}

        {/* Participants Dialog */}
        <Dialog
          open={participantsDialogOpen}
          onClose={handleCloseParticipantsDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
            Participants with Alerts ({selectedParticipants.length})
          </DialogTitle>
          <DialogContent>
            {selectedParticipants.length > 0 ? (
              <List>
                {selectedParticipants.map((participantId) => (
                  <ListItem key={participantId}>
                    <ListItemText primary={participantId} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No participants found
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseParticipantsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default StudiesMetricsTable;
